import { inject, injectable } from '@servicetitan/react-ioc';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../common/stores/special-settings.store';
import { ContactInfoMode } from '../utils/contact-info.validator';
import { contactInfoModeFromAllowed } from '../utils/contact-method-mode';

const permissionSettingsKey = 'permission-settings';
const signupContactMethodsKey = 'signup-contact-methods';

interface PermissionSettings {
    requireInviteToSignUp: boolean;
}

@injectable()
export class SettingsStore {
    @observable permissionSettings?: PermissionSettings;
    @observable loadingSettings: boolean = true;
    @observable failedToLoadSettings: boolean = false;
    @observable contactInfoMode: ContactInfoMode = 'email-only';

    constructor(
        @inject(SpecialSettingsStore) private readonly specialSettings: SpecialSettingsStore
    ) {
        makeObservable(this);
        this.loadPermissionSettings();
    }

    private async loadPermissionSettings() {
        try {
            const permissionSettings = await this.specialSettings.getAsync(permissionSettingsKey);

            /*
             * Derive contactInfoMode unconditionally — get() returns undefined on failed load,
             * and contactInfoModeFromAllowed(undefined) safely returns 'email-only' (AC-4).
             */
            const contactMethods = this.specialSettings.get<{ allowedContactMethods?: unknown }>(
                signupContactMethodsKey
            );
            runInAction(() => {
                this.contactInfoMode = contactInfoModeFromAllowed(
                    contactMethods?.allowedContactMethods
                );
            });

            if (this.specialSettings.failed) {
                runInAction(() => {
                    this.failedToLoadSettings = true;
                });
                return;
            }
            this.setPermissionsSettingsFormValues(permissionSettings);
        } finally {
            runInAction(() => {
                this.loadingSettings = false;
            });
        }
    }

    @action
    private setPermissionsSettingsFormValues(rawPermissionSettings: any) {
        const specialSettings = this.normalizeSpecialSettings(rawPermissionSettings);

        const permissionSettings: PermissionSettings = {
            requireInviteToSignUp: specialSettings?.requireInviteToSignUp ?? false,
        };
        this.permissionSettings = permissionSettings;
    }

    private normalizeSpecialSettings(rawFilter: any): PermissionSettings {
        if (typeof rawFilter === 'object' && rawFilter !== null) {
            return rawFilter as PermissionSettings;
        }
        return {} as PermissionSettings;
    }
}
